import axios from "axios";
import sampleFareQuoteData from "../fareQuote.json";
import { getUserIdentifier } from "../utils/identity";

// Use proxy in development, full URL in production
//https://s4nvnzwoc8.execute-api.ap-south-1.amazonaws.com/default/tbo_api_orchesterator/development test for
const API_BASE_URL =
  process.env.NODE_ENV === "development"
    ? "https://s4nvnzwoc8.execute-api.ap-south-1.amazonaws.com/default/tbo_api_orchesterator"
    : "https://s4nvnzwoc8.execute-api.ap-south-1.amazonaws.com/default/tbo_api_orchesterator";
const END_USER_IP = "125.63.76.234";
const FROM_SOURCE = `webbot_new:${getUserIdentifier()}`;
const REQUEST_TIMEOUT = 30000; // 30 seconds
const MAX_RETRIES = 3;
const RETRY_DELAY = 2000; // 2 seconds

const INVOICE_API_URL =
  process.env.NODE_ENV === "development"
    ? "/default/invoice-gen"
    : "https://929j7pyhu3.execute-api.ap-south-1.amazonaws.com/default/invoice-gen";

// Flag to enable/disable fallback mode
const USE_FALLBACK_ON_ERROR = true;

// Flag to toggle between live (false) and test (true) modes for flight API requests
export const FLIGHT_TEST = false;

/**
 * Special error class that includes fallback data
 * When this is thrown, UI can show modal with option to use fallback
 */
export class FallbackAvailableError extends Error {
  constructor(message, operationName, fallbackData, originalError) {
    super(message);
    this.name = 'FallbackAvailableError';
    this.operationName = operationName;
    this.fallbackData = fallbackData;
    this.originalError = originalError;
    this.errorDetails = originalError?.stack || originalError?.message || String(originalError);
  }
}

/**
 * Extract email from Firebase JWT token
 * @param {string} token - Firebase JWT token
 * @returns {string} - Email from token or empty string
 */
const extractEmailFromToken = (token) => {
  try {
    if (!token) return "";

    // JWT format: header.payload.signature
    const parts = token.split(".");
    if (parts.length !== 3) {
      console.warn("[extractEmailFromToken] Invalid token format (parts: " + parts.length + ")");
      return "";
    }

    // Decode the payload (Base64Url to Base64)
    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));

    const payload = JSON.parse(jsonPayload);

    // Return email, phone_number, or uid-based identifiers
    const identity = payload.email || payload.phone_number || payload.user_id || payload.sub || payload.uid || "";

    if (!identity) {
      console.warn("[extractEmailFromToken] No identity found in payload. Available keys:", Object.keys(payload));
    }

    return identity;
  } catch (error) {
    console.warn("[extractEmailFromToken] Extraction failed:", error.message);
    return "";
  }
};

/**
 * Generate dynamic From field based on user authentication
 * @param {string} userToken - Firebase JWT token
 * @returns {string} - Formatted From field (webbot_new:{email|phone} or webbot_new:anon)
 */
const generateFromField = (userToken) => {
  const userIdentifier = extractEmailFromToken(userToken);
  return userIdentifier ? `webbot_new:${userIdentifier}` : FROM_SOURCE;
};

/**
 * Generate sample fare rules response from the sample fare quote data
 * This simulates the fare rules API response structure
 */
const generateSampleFareRulesResponse = () => {
  // console.log("⚠️ Using sample mock data for Fare Rules Response");
  return {
    status: 200,
    data: {
      FareRules: sampleFareQuoteData.data?.Results?.FareRules || [],
      MiniFareRules: sampleFareQuoteData.data?.Results?.MiniFareRules || []
    },
    rc_booking_id: "sample_rc_booking_id_" + Date.now(),
    message: "",
    quick_replies: [],
    more_replies_await: false
  };
};

// Create axios instance
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: REQUEST_TIMEOUT,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor for logging
apiClient.interceptors.request.use(
  (config) => {
    // console.log("[API Request]", config.method.toUpperCase(), config.url);
    return config;
  },
  (error) => {
    console.error("[API Request Error]", error);
    return Promise.reject(error);
  }
);

// Response interceptor for logging and error handling
apiClient.interceptors.response.use(
  (response) => {
    // console.log("[API Response]", response.status, response.config.url);
    return response;
  },
  (error) => {
    console.error("[API Error]", error.message);
    return Promise.reject(error);
  }
);

/**
 * Retry logic for failed requests
 */
const retryRequest = async (requestFn, retries = MAX_RETRIES) => {
  try {
    return await requestFn();
  } catch (error) {
    if (
      retries > 0 &&
      (error.code === "ECONNABORTED" || error.code === "ERR_NETWORK")
    ) {
      // console.log(`Retrying... (${MAX_RETRIES - retries + 1}/${MAX_RETRIES})`);
      await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));
      return retryRequest(requestFn, retries - 1);
    }
    throw error;
  }
};

/**
 * Fetch Fare Quote
 * @param {string} trace_id - Trace ID from search
 * @param {string} result_idx - Result index
 * @param {string} userToken - Firebase auth token
 * @returns {Promise} API response
 */
export const fetchFareQuote = async (trace_id, result_idx, userToken) => {
  try {
    // Generate dynamic From field based on user authentication
    const dynamicFrom = generateFromField(userToken);

    const requestBody = {
      intent: "fareQuote",
      flight_test: FLIGHT_TEST,
      TraceId: trace_id,
      ResultIndex: result_idx,
      EndUserIp: END_USER_IP,
      From: dynamicFrom,
      headers: {
        Authorization: `Bearer ${userToken}`,
      },
    };

    // console.log("[fetchFareQuote] Request body:", requestBody);
    // console.log("[fetchFareQuote] Dynamic From field:", dynamicFrom);

    const requestFn = () => apiClient.post("", requestBody);

    const response = await retryRequest(requestFn);
    const responseData = response.data;

    // Validate response structure
    if (!responseData) {
      throw new Error("Invalid response: No data received");
    }

    if (responseData.status !== 200) {
      const error = new Error(responseData.message || "Failed to fetch fare quote");
      error.status = responseData.status;
      throw error;
    }

    if (!responseData.data?.Results) {
      throw new Error("Invalid response structure: Missing Results");
    }

    return responseData;
  } catch (error) {
    console.error("Error fetching fare quote:", error);

    // Enhanced error messages
    let errorMessage;
    if (error.code === "ECONNABORTED" || error.code === "ERR_NETWORK") {
      errorMessage = "Request timed out. Please check your internet connection and try again.";
    } else if (error.response) {
      errorMessage = error.response.data?.message || "Server error occurred";
    } else if (error.request) {
      errorMessage = "No response from server. Please check your internet connection.";
    } else {
      errorMessage = error.message || "An unexpected error occurred";
    }

    // Throw special error with fallback data if enabled
    if (USE_FALLBACK_ON_ERROR) {
      console.warn("⚠️ Backend error detected. Fallback data available.");
      throw new FallbackAvailableError(
        errorMessage,
        "Fetch Fare Quote",
        sampleFareQuoteData,
        error
      );
    }

    throw new Error(errorMessage);
  }
};

/**
 * Fetch Fare Rules
 * @param {string} trace_id - Trace ID from search
 * @param {string} result_idx - Result index
 * @param {string} userToken - Firebase auth token
 * @param {string} rc_booking_id - RC Booking ID from fetchFareQuote response
 * @returns {Promise} API response
 */
export const fetchFareRules = async (trace_id, result_idx, userToken, rc_booking_id = null) => {
  try {
    // Generate dynamic From field based on user authentication
    const dynamicFrom = generateFromField(userToken);

    const requestBody = {
      intent: "fareRules",
      flight_test: FLIGHT_TEST,
      TraceId: trace_id,
      ResultIndex: result_idx,
      EndUserIp: END_USER_IP,
      From: dynamicFrom,
      headers: {
        Authorization: `Bearer ${userToken}`,
      },
    };

    // Add rc_booking_id if provided
    if (rc_booking_id) {
      requestBody.rc_booking_id = rc_booking_id;
    }

    // console.log("[fetchFareRules] Request body:", requestBody);
    // console.log("[fetchFareRules] Dynamic From field:", dynamicFrom);
    // console.log("[fetchFareRules] RC Booking ID:", rc_booking_id);

    const requestFn = () => apiClient.post("", requestBody);

    const response = await retryRequest(requestFn);
    const responseData = response.data;

    // Validate response structure
    if (!responseData) {
      throw new Error("Invalid response: No data received");
    }

    if (responseData.status !== 200) {
      const error = new Error(responseData.message || "Failed to fetch fare rules");
      error.status = responseData.status;
      throw error;
    }

    if (!responseData.data?.FareRules) {
      throw new Error("Invalid response structure: Missing FareRules");
    }

    return responseData;
  } catch (error) {
    console.error("Error fetching fare rules:", error);

    // Enhanced error messages
    let errorMessage;
    if (error.code === "ECONNABORTED" || error.code === "ERR_NETWORK") {
      errorMessage = "Request timed out. Please check your internet connection and try again.";
    } else if (error.response) {
      errorMessage = error.response.data?.message || "Server error occurred";
    } else if (error.request) {
      errorMessage = "No response from server. Please check your internet connection.";
    } else {
      errorMessage = error.message || "An unexpected error occurred";
    }

    // Throw special error with fallback data if enabled
    if (USE_FALLBACK_ON_ERROR) {
      console.warn("⚠️ Backend error detected. Fallback data available.");
      throw new FallbackAvailableError(
        errorMessage,
        "Fetch Fare Rules",
        generateSampleFareRulesResponse(),
        error
      );
    }

    throw new Error(errorMessage);
  }
};

/**
 * Create Order
 * @param {string} rc_booking_id - RouteChef booking ID
 * @param {string} trace_id - Trace ID from search
 * @param {string} result_idx - Result index
 * @param {string} amount - Booking amount
 * @param {string} userToken - Firebase auth token
 * @param {string} userContact - User's contact (phone/email) for From field
 * @returns {Promise} API response
 */
export const createOrder = async (
  rc_booking_id,
  trace_id,
  result_idx,
  amount,
  userToken,
  userContact,
  phone = "",
  email = "",
  passengers = [],
  is_lcc = true,
  billing_details = null
) => {
  try {
    // Format From field: webbot_new:{email_or_phone}
    // If userContact is provided, use it; otherwise try extracting from token
    // Determine effective user identifier (Email/Phone from token OR anon ID like Search Form)
    const userIdentifierFromToken = extractEmailFromToken(userToken);
    const effectiveContact = userContact || userIdentifierFromToken;
    const formattedFrom = userIdentifierFromToken
      ? `webbot_new:${userIdentifierFromToken}`
      : `webbot_new:${getUserIdentifier()}`;

    // console.log("[createOrder] userContact received:", userContact);
    // console.log("[createOrder] userIdentifierFromToken:", userIdentifierFromToken);
    // console.log("[createOrder] formattedFrom:", formattedFrom);
    // console.log("[createOrder] passengers:", passengers);
    // console.log("[createOrder] is_lcc:", is_lcc);

    const requestBody = {
      intent: "create_order",
      flight_test: FLIGHT_TEST,
      rc_booking_id: rc_booking_id,
      EndUserIp: END_USER_IP,
      TraceId: trace_id,
      ResultIndex: result_idx,
      amount: amount,
      currency: "INR",
      From: formattedFrom,
      phone: phone || (effectiveContact?.includes?.("@") ? "" : (typeof effectiveContact === 'string' ? effectiveContact : "")),
      email: email || (effectiveContact?.includes?.("@") ? effectiveContact : ""),
      Passengers: passengers,
      is_lcc: is_lcc,
      billing_details: billing_details,
      headers: {
        Authorization: `Bearer ${userToken}`,
      },
    };

    // console.log("[createOrder] Request body:", requestBody);

    const requestFn = () => apiClient.post("", requestBody);

    const response = await retryRequest(requestFn);
    const responseData = response.data;

    // Validate response structure
    if (!responseData) {
      throw new Error("Invalid response: No data received");
    }

    // Note: Accept both 200 and 201 as success codes for order creation
    if (responseData.status !== 200 && responseData.status !== 201) {
      console.warn(
        `[createOrder] Non-success status code: ${responseData.status}`
      );
      // Only throw if there's an explicit error message
      if (
        responseData.message &&
        responseData.message.toLowerCase().includes("error")
      ) {
        const error = new Error(responseData.message);
        error.status = responseData.status;
        throw error;
      }
    }

    // Check if we have the required data structure
    if (!responseData.data) {
      throw new Error(
        responseData.message || "Invalid response structure: Missing data"
      );
    }

    // console.log("> response data: ", responseData);
    return responseData;
  } catch (error) {
    console.error("Error creating order:", error);

    // Enhanced error messages
    let errorMessage;
    if (error.code === "ECONNABORTED" || error.code === "ERR_NETWORK") {
      errorMessage = "Request timed out. Please check your internet connection and try again.";
    } else if (error.response) {
      errorMessage = error.response.data?.message || "Server error occurred";
    } else if (error.request) {
      errorMessage = "No response from server. Please check your internet connection.";
    } else {
      errorMessage = error.message || "An unexpected error occurred";
    }

    // Mock response for testing if backend fails
    const mockOrderData = {
      status: 200,
      data: {
        rzp_order_id: "order_MockOrder123",
        rzp_key_id: "rzp_test_RvwWWIyLjhX3CC",
        amount: Number(amount),
        currency: "INR",
        status: "created",
        rc_booking_id: rc_booking_id
      },
      message: "Payment order created (MOCK)",
      rc_booking_id: rc_booking_id
    };

    // Throw special error with fallback data if enabled
    if (USE_FALLBACK_ON_ERROR) {
      console.warn("⚠️ Backend error detected. Fallback data available.");
      throw new FallbackAvailableError(
        errorMessage,
        "Create Payment Order",
        mockOrderData,
        error
      );
    }

    throw new Error(errorMessage);
  }
};

/**
 * Get Status
 * @param {string} rc_booking_id - RouteChef booking ID
 * @param {string} trace_id - Trace ID from search
 * @param {string} result_idx - Result index
 * @param {string} amount - Booking amount
 * @param {string} userToken - Firebase auth token
 * @param {string} userContact - User's contact (phone/email) for From field
 * @returns {Promise} API response
 */
export const getStatus = async (
  rc_booking_id,
  trace_id,
  result_idx,
  amount,
  userToken,
  userContact,
  phone = "",
  email = ""
) => {
  try {
    // Determine effective user identifier (Email/Phone from token OR anon ID like Search Form)
    const userIdentifierFromToken = extractEmailFromToken(userToken);
    const effectiveContact = userContact || userIdentifierFromToken;
    const formattedFrom = userIdentifierFromToken
      ? `webbot_new:${userIdentifierFromToken}`
      : `webbot_new:${getUserIdentifier()}`;

    // console.log("[getStatus] userContact received:", userContact);
    // console.log("[getStatus] userIdentifierFromToken:", userIdentifierFromToken);
    // console.log("[getStatus] formattedFrom:", formattedFrom);

    const requestBody = {
      intent: "get_status",
      flight_test: FLIGHT_TEST,
      rc_booking_id: rc_booking_id,
      EndUserIp: END_USER_IP,
      TraceId: trace_id,
      ResultIndex: result_idx,
      amount: amount,
      From: formattedFrom,
      phone: phone || (effectiveContact?.includes?.("@") ? "" : (typeof effectiveContact === 'string' ? effectiveContact : "")),
      email: email || (effectiveContact?.includes?.("@") ? effectiveContact : ""),
      headers: {
        ...(userToken ? { Authorization: `Bearer ${userToken}` } : {}),
      },
    };

    // console.log("[getStatus] Request body:", requestBody);

    const requestFn = () => apiClient.post("", requestBody);

    const response = await retryRequest(requestFn);
    const responseData = response.data;

    // Validate response structure
    if (!responseData) {
      throw new Error("Invalid response: No data received");
    }

    // Note: The API may return status 400 or other codes with valid data
    // We should not throw an error if we have valid data, just log it
    if (responseData.status !== 200 && responseData.status !== 400) {
      console.warn(
        `[getStatus] Non-standard status code: ${responseData.status}`
      );
    }

    // Check if we have the required data structure
    if (!responseData.data) {
      const error = new Error(
        responseData.message || "Invalid response structure: Missing data"
      );
      error.status = responseData.status;
      throw error;
    }

    return responseData;
  } catch (error) {
    console.error("Error getting status:", error);

    // Enhanced error messages
    let errorMessage;
    if (error.code === "ECONNABORTED" || error.code === "ERR_NETWORK") {
      errorMessage = "Request timed out. Please check your internet connection and try again.";
    } else if (error.response) {
      errorMessage = error.response.data?.message || "Server error occurred";
    } else if (error.request) {
      errorMessage = "No response from server. Please check your internet connection.";
    } else {
      errorMessage = error.message || "An unexpected error occurred";
    }

    // Mock pending response
    const mockStatusData = {
      status: 200,
      data: {
        bookings: [
          {
            status: "PAYMENT_PENDING",
            rc_booking_id: rc_booking_id,
            payment_link_url: "https://razorpay.com/demo",
            amount: Number(amount),
            ticket_url: null,
            payment_status: "PENDING",
            pnr: null
          }
        ]
      },
      message: "Payment is pending (MOCK FALLBACK)",
    };

    // Throw special error with fallback data if enabled
    if (USE_FALLBACK_ON_ERROR) {
      console.warn("⚠️ Backend error detected. Fallback data available.");
      throw new FallbackAvailableError(
        errorMessage,
        "Get Booking Status",
        mockStatusData,
        error
      );
    }

    throw new Error(errorMessage);
  }
};
/**
 * Update Booking Status
 * @param {string} rc_booking_id 
 * @param {string} user_id 
 * @param {string} new_status 
 * @param {object} extra_data 
 * @returns {Promise}
 */
export const updateBookingStatus = async (
  rc_booking_id,
  user_id,
  new_status,
  extra_data = {}
) => {
  try {
    const requestBody = {
      intent: "updatebookingstatus",
      flight_test: FLIGHT_TEST,
      rc_booking_id,
      user_id,
      new_status,
      ...extra_data,
      From: `webbot_new:${user_id}`,
      EndUserIp: END_USER_IP
    };

    // console.log("[updateBookingStatus] Request body:", requestBody);
    const response = await apiClient.post("", requestBody);
    return response.data;
  } catch (error) {
    console.error("[updateBookingStatus] Error:", error);
    throw error;
  }
};

/**
 * Get Booking History (paginated)
 * @param {string} userContact - User email or phone number
 * @param {number} limit - Maximum items per page (default 20)
 * @param {object|null} lastEvaluatedKey - For pagination cursor (null for first page)
 * @param {string|null} rcBookingId - Optional specific booking ID to fetch
 * @returns {Promise<{bookings: Array, last_evaluated_key: object|null}>}
 */
export const getBookingHistory = async (
  userContact,
  limit = 20,
  lastEvaluatedKey = null,
  rcBookingId = null
) => {
  // console.log("[getBookingHistory] START userContact:", userContact, "limit:", limit);

  try {
    const requestBody = {
      intent: "bookinghistory",
      flight_test: FLIGHT_TEST,
      From: `webbot_new:${userContact}`,
      EndUserIp: END_USER_IP,
      limit: limit
    };

    // Add pagination cursor if provided
    if (lastEvaluatedKey) {
      requestBody.last_evaluated_key = lastEvaluatedKey;
    }

    // Add specific booking ID if provided
    if (rcBookingId) {
      requestBody.rc_booking_id = rcBookingId;
    }

    // console.log("[getBookingHistory] Request body:", requestBody);

    const response = await retryRequest(
      () => apiClient.post("", requestBody),
      MAX_RETRIES,
      RETRY_DELAY
    );

    // console.log("[getBookingHistory] Response:", response.data);

    // Parse response
    const responseData = response.data;

    if (responseData?.status !== 200) {
      throw new Error(responseData?.message || "Failed to fetch booking history");
    }

    return {
      bookings: responseData?.data?.bookings || [],
      last_evaluated_key: responseData?.data?.last_evaluated_key || null,
      message: responseData?.message || ""
    };

  } catch (error) {
    console.error("[getBookingHistory] Error:", error);

    // Enhanced error messages
    let errorMessage;
    if (error.code === "ECONNABORTED" || error.code === "ERR_NETWORK") {
      errorMessage = "Request timed out. Please check your internet connection.";
    } else if (error.response) {
      errorMessage = error.response.data?.message || "Server error occurred";
    } else if (error.request) {
      errorMessage = "No response from server. Please check your internet connection.";
    } else {
      errorMessage = error.message || "An unexpected error occurred";
    }

    // Return empty state with error for graceful handling
    if (USE_FALLBACK_ON_ERROR) {
      console.warn("⚠️ [getBookingHistory] Using empty fallback due to error");
      return {
        bookings: [],
        last_evaluated_key: null,
        error: errorMessage
      };
    }

    throw new Error(errorMessage);
  }
};

/**
 * Get Cancellation Charges
 * @param {number} bookingId - TBO Booking ID
 * @param {string} userToken - Firebase auth token
 * @returns {Promise} API response
 */
export const getCancellationCharges = async (bookingId, userToken) => {
  try {
    const userId = extractEmailFromToken(userToken);
    const requestBody = {
      intent: "getcancellationcharges",
      flight_test: FLIGHT_TEST,
      booking_id: bookingId,
      request_type: 1, // 1 for Cancellation
      booking_mode: 5, // 5 for API
      user_id: userId,
      From: generateFromField(userToken),
      EndUserIp: END_USER_IP,
      headers: {
        Authorization: `Bearer ${userToken}`,
      },
    };

    // console.log("[getCancellationCharges] Request body:", requestBody);
    const response = await apiClient.post("", requestBody);
    return response.data;
  } catch (error) {
    console.error("Error fetching cancellation charges:", error);
    throw error;
  }
};

/**
 * Send Change Request (Cancellation/Reissue)
 * @param {object} params - Request parameters
 * @returns {Promise} API response
 */
export const sendChangeRequest = async ({
  bookingId,
  requestType,
  cancellationType,
  remarks,
  sectors = [],
  ticketIds = [],
  userToken
}) => {
  try {
    const userId = extractEmailFromToken(userToken);
    const requestBody = {
      intent: "sendchangerequest",
      flight_test: FLIGHT_TEST,
      booking_id: bookingId,
      user_id: userId,
      From: generateFromField(userToken),
      request_type: requestType,
      cancellation_type: cancellationType,
      remarks: remarks,
      sectors: sectors,
      ticket_id: ticketIds,
      EndUserIp: END_USER_IP,
      headers: {
        Authorization: `Bearer ${userToken}`,
      },
    };

    // console.log("[sendChangeRequest] Request body:", requestBody);
    const response = await apiClient.post("", requestBody);
    return response.data;
  } catch (error) {
    console.error("Error sending change request:", error);
    throw error;
  }
};

/**
 * Get Change Request Status
 * @param {number} changeRequestId - TBO Change Request ID
 * @param {string} userToken - Firebase auth token
 * @returns {Promise} API response
 */
export const getChangeRequestStatus = async (changeRequestId, userToken) => {
  try {
    const userId = extractEmailFromToken(userToken);
    const requestBody = {
      intent: "getchangerequeststatus",
      flight_test: FLIGHT_TEST,
      change_request_id: changeRequestId,
      user_id: userId,
      From: generateFromField(userToken),
      EndUserIp: END_USER_IP,
      headers: {
        Authorization: `Bearer ${userToken}`,
      },
    };

    // console.log("[getChangeRequestStatus] Request body:", requestBody);
    const response = await apiClient.post("", requestBody);
    return response.data;
  } catch (error) {
    console.error("Error fetching change request status:", error);
    throw error;
  }
};
/**
 * Send Support Request
 * @param {object} supportData - { name, email, phone, message, rc_booking_id, From }
 * @returns {Promise}
 */
export const sendSupportRequest = async (supportData) => {
  try {
    const requestBody = {
      intent: "support_request",
      flight_test: FLIGHT_TEST,
      ...supportData,
      EndUserIp: END_USER_IP,
    };

    // console.log("[sendSupportRequest] Request body:", requestBody);

    const requestFn = () => apiClient.post("", requestBody);
    const response = await retryRequest(requestFn);

    return response.data;
  } catch (error) {
    console.error("Error sending support request:", error);
    throw error;
  }
};

/**
 * Generate Invoice
 * @param {object} params - Invoice generation parameters
 * @returns {Promise} API response
 */
export const generateInvoice = async ({
  ticket_details,
  platform_fee,
  gst_rate,
  rc_booking_id,
  user_id,
  document_type = "invoice"
}) => {
  try {
    const requestBody = {
      ticket_details,
      routechef_fee: {
        platform_fee,
        gst_rate
      },
      rc_booking_id,
      user_id,
      document_type
    };

    // console.log("[generateInvoice] Request body:", requestBody);
    const response = await axios.post(INVOICE_API_URL, requestBody, {
      timeout: 60000
    });
    return response.data;
  } catch (error) {
    console.error("Error generating invoice:", error);
    throw error;
  }
};

/**
 * Verify GSTIN
 * @param {string} gstin - User's GSTIN
 * @returns {Promise} API response
 */
export const verifyGSTIN = async (gstin) => {
  try {
    const requestBody = {
      intent: "gst_verify",
      gstin: gstin
    };

    const requestFn = () => apiClient.post("", requestBody);
    const response = await retryRequest(requestFn);

    return response.data;
  } catch (error) {
    console.error("Error verifying GSTIN:", error);
    throw error;
  }
};
